import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getEmotionalPerformance } from '../store/analytics/thunks';
import { Chart } from 'react-google-charts';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Center,
  VStack,
  Badge,
  useColorMode,
} from '@chakra-ui/react';
import getSymbolFromCurrency from 'currency-symbol-map';
import AnalyticsSkeleton from './AnalyticsSkeleton';

import '../styles/profile.css';

const EMOTIONAL_COLORS = {
  calm: { bg: 'green.400', scheme: 'green' },
  anxious: { bg: 'orange.400', scheme: 'orange' },
  excited: { bg: 'yellow.400', scheme: 'yellow' },
  fearful: { bg: 'purple.400', scheme: 'purple' },
  revenge: { bg: 'red.400', scheme: 'red' },
};

export default function EmotionalAnalytics({ user }) {
  const dispatch = useDispatch();
  const { colorMode } = useColorMode();
  const emotionalPerformance = useSelector((state) => state.analytics.emotionalPerformance);
  const loading = useSelector((state) => state.ui.loading);
  const preferences = useSelector((state) => state.user.preferences);

  const currencySymbol = getSymbolFromCurrency(preferences?.preferred_currency || 'USD') || '$';

  useEffect(() => {
    dispatch(getEmotionalPerformance());
  }, [dispatch]);

  const barChartData = [
    ['Emotional State', 'Avg P&L', { role: 'style' }],
    ...emotionalPerformance.map((e) => [
      e.emotional_state.charAt(0).toUpperCase() + e.emotional_state.slice(1),
      e.avg_pnl,
      e.avg_pnl >= 0 ? '#48BB78' : '#FC8181',
    ]),
  ];

  const barChartOptions = {
    title: 'Average P&L by Emotional State',
    chartArea: { width: '60%', height: '75%' },
    height: '100%',
    width: '100%',
    backgroundColor: colorMode === 'light' ? '#FDFDFD' : '#1A202C',
    titleTextStyle: {
      color: colorMode === 'light' ? '#636363' : '#E2E8F0',
      fontName: 'Open Sans',
      bold: true,
      fontSize: 16,
    },
    hAxis: {
      title: `Avg P&L (${currencySymbol})`,
      titleTextStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    vAxis: {
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    legend: 'none',
  };

  if (loading && emotionalPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <VStack spacing={4} p={4} align="stretch" w="full">
            <AnalyticsSkeleton heatmapColumns={{ base: 2, sm: 3, md: 5 }} heatmapCount={5} />
          </VStack>
        </Box>
      </Flex>
    );
  }

  if (emotionalPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <Center h="60vh">
            <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
              No trade review data yet. Complete post-trade reviews to see emotional state analytics.
            </Text>
          </Center>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
      <Box flex="auto" overflowX="auto" overflowY="auto">
        <VStack spacing={4} p={4} align="stretch" w="full">
          <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
            Emotional State Analytics
          </Heading>

          {/* Cards */}
          <Box>
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Performance by Emotional State
            </Heading>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={3}>
              {emotionalPerformance.map((e) => {
                const colors = EMOTIONAL_COLORS[e.emotional_state] || { bg: 'gray.400', scheme: 'gray' };
                const isPositive = e.total_pnl >= 0;
                return (
                  <Box
                    key={e.emotional_state}
                    p={3}
                    rounded="md"
                    bg={colors.bg}
                    color="white"
                    textAlign="center"
                  >
                    <Text fontWeight="bold" fontSize="sm" textTransform="capitalize">
                      {e.emotional_state}
                    </Text>
                    <Text fontSize="xs">
                      Avg: {isPositive ? '+' : ''}{currencySymbol}{e.avg_pnl.toFixed(2)}
                    </Text>
                    <Text fontSize="xs" opacity={0.9}>
                      {e.win_rate !== null ? `${e.win_rate}% WR` : 'N/A'}
                    </Text>
                    <Text fontSize="xs" opacity={0.85}>
                      {e.num_trades} trade{e.num_trades !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>

          {/* Bar Chart */}
          <Box
            bg={colorMode === 'light' ? 'white' : 'gray.700'}
            rounded="lg"
            boxShadow="sm"
            p={4}
            h="400px"
          >
            <Chart
              chartType="BarChart"
              width="100%"
              height="100%"
              data={barChartData}
              options={barChartOptions}
            />
          </Box>

          {/* Table */}
          <Box overflowX="auto">
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Breakdown
            </Heading>
            <Box
              as="table"
              w="100%"
              bg={colorMode === 'light' ? 'white' : 'gray.700'}
              rounded="lg"
              boxShadow="sm"
              overflow="hidden"
              fontSize="sm"
            >
              <Box as="thead" bg={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                <Box as="tr">
                  {['State', 'Trades', 'Wins', 'Losses', 'Win Rate', 'Total P&L', 'Avg P&L'].map((h) => (
                    <Box
                      as="th"
                      key={h}
                      p={3}
                      textAlign="left"
                      fontWeight="semibold"
                      color={colorMode === 'light' ? 'gray.600' : 'gray.200'}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {emotionalPerformance.map((e) => {
                  const colors = EMOTIONAL_COLORS[e.emotional_state] || { scheme: 'gray' };
                  return (
                    <Box
                      as="tr"
                      key={e.emotional_state}
                      _hover={{
                        bg: colorMode === 'light' ? 'gray.50' : 'gray.600',
                      }}
                      borderBottomWidth="1px"
                      borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}
                    >
                      <Box as="td" p={3}>
                        <Badge colorScheme={colors.scheme} textTransform="capitalize">
                          {e.emotional_state}
                        </Badge>
                      </Box>
                      <Box as="td" p={3}>{e.num_trades}</Box>
                      <Box as="td" p={3}>{e.num_wins}</Box>
                      <Box as="td" p={3}>{e.num_losses}</Box>
                      <Box as="td" p={3}>{e.win_rate !== null ? `${e.win_rate}%` : '-'}</Box>
                      <Box
                        as="td"
                        p={3}
                        color={e.total_pnl >= 0 ? 'green.500' : 'red.500'}
                        fontWeight="semibold"
                      >
                        {e.total_pnl >= 0 ? '+' : ''}{currencySymbol}{e.total_pnl.toFixed(2)}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        color={e.avg_pnl >= 0 ? 'green.500' : 'red.500'}
                      >
                        {e.avg_pnl >= 0 ? '+' : ''}{currencySymbol}{e.avg_pnl.toFixed(2)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
}
